import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { auth, provider, signInWithPopup } from '../services/firebase';
import logoImage from '../assets/acts-full-logo.svg';
import googleIcon from '../assets/google-96.svg';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function FastSignUp() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Call the backend endpoint to verify fast signup token
  const { data: verifiedData, isLoading: isVerifying, error: verifyError } = trpc.auth.verifyFastSignupToken.useQuery(
    { token },
    {
      enabled: !!token,
      retry: false,
    }
  );

  const createUserWithGoogleMutation = trpc.user.createUserWithGoogle.useMutation();

  const handleGoogleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifiedData?.churchId) {
      setError('Invalid registration context.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      console.log('Opening Google sign-in popup...');
      const userCredential = await signInWithPopup(auth, provider);
      console.log('Google sign-in successful:', userCredential.user.email);

      createUserWithGoogleMutation.mutate(
        {
          email: userCredential.user.email!,
          password: 'GOOGLE_AUTH',
          first_name: verifiedData.memberName || 'Google User',
          last_name: verifiedData.memberSurname || '',
          ChurchID: verifiedData.churchId,
          MemID: verifiedData.mappedMemId ?? undefined,
          StructID: verifiedData.structId ?? undefined,
        },
        {
          onSuccess: (res) => {
            if (res) {
              console.log('User created successfully:', res);
              window.location.href = '/confirmation';
            }
          },
          onError: (error) => {
            console.error('Error creating user:', error);
            if (error.data?.code === 'CONFLICT') {
              setError('This email address is already in use.');
            } else {
              setError('Failed to create account. Please try again.');
            }
          },
        }
      );
    } catch (err: any) {
      console.error('Google sign-up error:', err);
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setError('Google sign-up cancelled.');
      } else {
        setError(err.message || 'Google registration failed.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading Token State
  if (isVerifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-100 to-blue-200 p-4">
        <div className="p-8 bg-white/80 backdrop-blur-md border border-slate-200 rounded-3xl shadow-xl text-center max-w-md w-full">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-slate-800">Verifying Link</h2>
          <p className="text-slate-500 text-sm mt-1">Please wait while we check your registration link...</p>
        </div>
      </div>
    );
  }

  // Error/Expired State
  if (verifyError || !verifiedData?.valid || !token) {
    const errorMessage = verifyError?.message || (!token ? 'No registration token provided.' : 'This invitation link has expired (15-minute limit) or is invalid. Please request a new registration QR code from your administrator or leader.');
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-100 to-blue-200 p-4">
        <div className="p-8 bg-white/80 backdrop-blur-md border border-slate-200 rounded-3xl shadow-xl text-center max-w-md w-full">
          <div className="h-16 w-16 mx-auto mb-4 bg-rose-100 rounded-full flex items-center justify-center text-rose-600 border border-rose-200">
            <ErrorOutlineIcon className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Registration Link Error</h1>
          <p className="text-slate-500 mt-2 text-sm whitespace-pre-line">
            {errorMessage}
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 w-full px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/25 transition-all duration-300 active:scale-95"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-blue-100 to-indigo-200 p-4 font-sans">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-2xl border border-slate-200/50 flex flex-col items-center">
        
        {/* Logo */}
        <img src={logoImage} alt="Acts Camp Logo" className="h-14 mb-6" />

        <div className="text-center mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">Fast Sign Up</h2>
          <p className="text-slate-500 text-sm mt-1">
            Registering under <span className="font-semibold text-indigo-600">{verifiedData.churchName || `Church ID ${verifiedData.churchId}`}</span>
          </p>
          {verifiedData.mappedMemId && (
            <div className="mt-2 inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-semibold border border-emerald-200">
              <CheckCircleOutlineIcon style={{ fontSize: 14 }} />
              Member Profile Linked
            </div>
          )}
        </div>

        {error && (
          <div className="w-full bg-rose-50 border border-rose-200 text-rose-600 px-4 py-3 rounded-xl text-xs mb-4 font-medium">
            {error}
          </div>
        )}

        {verifiedData.memberName && (
          <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4 text-center mb-6">
            <span className="text-xs text-slate-500 uppercase tracking-wider block font-bold mb-1">REGISTERING AS</span>
            <span className="text-lg font-bold text-slate-800">{verifiedData.memberName} {verifiedData.memberSurname}</span>
          </div>
        )}

        <form onSubmit={handleGoogleSignUp} className="w-full">
          <button
            type="submit"
            disabled={isLoading || createUserWithGoogleMutation.isPending}
            className="w-full py-3.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-bold flex items-center justify-center gap-3 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 active:scale-95"
          >
            <img src={googleIcon} alt="Google" className="h-5 w-5" />
            {isLoading || createUserWithGoogleMutation.isPending ? 'Processing...' : 'Sign up with Google'}
          </button>
        </form>

        <p className="text-slate-400 text-[11px] text-center mt-6">
          By signing up, you agree to our terms and conditions. The signup link expires in 15 minutes.
        </p>

      </div>
    </div>
  );
}
